/**
 * Mode Maintenance (docs/PANDUAN_MODE_MAINTENANCE.md).
 * Pengaturan disimpan di tabel Supabase `pengaturan_maintenance` (satu baris, id = 1).
 */

export interface PengaturanMaintenance {
  id: number;
  aktif: boolean;
  mulai: string | null;
  selesai: string | null;
  selesai_otomatis: boolean;
  judul: string;
  pesan: string;
  kontak_wa: string | null;
  updated_by: string | null;
  updated_at: string | null;
}

export type StatusMaintenance = 'nonaktif' | 'terjadwal' | 'berlangsung' | 'berakhir';

export const MAINTENANCE_TABLE = 'pengaturan_maintenance';
export const MAINTENANCE_SETTINGS_PATH = '/portal-guru/admin/pengaturan/maintenance';

export const DEFAULT_JUDUL = 'Sedang Dalam Pemeliharaan';
export const DEFAULT_PESAN =
  'Kami sedang meningkatkan sistem agar Portal MIAS lebih cepat dan stabil. Mohon coba kembali beberapa saat lagi.';

const toMs = (iso: string | null): number | null => {
  if (!iso) return null;
  const ms = new Date(iso).getTime();
  return Number.isNaN(ms) ? null : ms;
};

/**
 * - nonaktif    : saklar mati.
 * - terjadwal   : saklar nyala, waktu mulai belum tiba (aplikasi masih normal, tampil pengumuman).
 * - berlangsung : halaman maintenance tampil.
 * - berakhir    : selesai_otomatis dan waktu selesai sudah lewat (aplikasi normal lagi).
 */
export const getStatusMaintenance = (
  p: PengaturanMaintenance | null,
  now: number = Date.now()
): StatusMaintenance => {
  if (!p || !p.aktif) return 'nonaktif';
  const mulai = toMs(p.mulai);
  const selesai = toMs(p.selesai);
  if (mulai !== null && now < mulai) return 'terjadwal';
  if (p.selesai_otomatis && selesai !== null && now >= selesai) return 'berakhir';
  return 'berlangsung';
};

/** Waktu (ms) status berikutnya berubah dengan sendirinya, untuk memasang timer. */
export const getWaktuPerubahanBerikutnya = (
  p: PengaturanMaintenance | null,
  now: number = Date.now()
): number | null => {
  if (!p || !p.aktif) return null;
  const kandidat = [toMs(p.mulai), p.selesai_otomatis ? toMs(p.selesai) : null]
    .filter((t): t is number => t !== null && t > now);
  return kandidat.length ? Math.min(...kandidat) : null;
};

const WIB = 'Asia/Jakarta';

export const formatWaktuWIB = (iso: string | null): string => {
  const ms = toMs(iso);
  if (ms === null) return '-';
  const teks = new Date(ms).toLocaleString('id-ID', {
    timeZone: WIB,
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
  return `${teks} WIB`;
};

export const formatJamWIB = (iso: string | null): string => {
  const ms = toMs(iso);
  if (ms === null) return '-';
  return `${new Date(ms).toLocaleTimeString('id-ID', { timeZone: WIB, hour: '2-digit', minute: '2-digit' })} WIB`;
};

/** Nomor WA ke format wa.me (62xxxxxxxx). */
export const normalisasiNomorWa = (nomor: string | null | undefined): string | null => {
  const angka = (nomor || '').replace(/\D/g, '');
  if (angka.length < 9) return null;
  if (angka.startsWith('0')) return `62${angka.slice(1)}`;
  return angka;
};

/** ISO <-> nilai input datetime-local (zona waktu perangkat). */
export const isoKeInputLokal = (iso: string | null): string => {
  const ms = toMs(iso);
  if (ms === null) return '';
  const d = new Date(ms);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

export const inputLokalKeIso = (nilai: string): string | null => {
  if (!nilai) return null;
  const d = new Date(nilai);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
};
